import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { cfpBaseUrl } from '../../network/urls';
import { resolveScopedTableOfficeCode, resolveTableApiOfficeLevel } from '../../utils/adminCompany';


/** -1 = Descending, 1 = Ascending (enc/getAllApplications sortOrder) */
export const NC_SORT_ORDER_DESC = -1;
export const NC_SORT_ORDER_ASC = 1;

const initialState = {
    data: [],
    currentPage: 1,
    pageSize: 20,
    dateFrom: '',
    dateTo: '',
    loadFrom: '',
    loadTo: '',
    applicationProgressId: '',
    objectionStatus: '',
    sortOrder: NC_SORT_ORDER_DESC,
    totalPages: null,
    totalRecords: null,
    isLoading: true,
    error: null,
    // Only the latest fetch may write data (see extraReducers)
    latestRequestId: null,
}

// Define an async thunk for fetching paginated data
export const fetchPaginatedNewConnectionNormal = createAsyncThunk(
    'generalServices/fetchNormal',
    async ({ page, pageSize, dateFrom, dateTo, loadFrom, loadTo, applicationProgressId, officeCode, officeLevel, sortOrder }, { getState }) => {
        const { auth } = getState();
        const { token, code, hierarchy, cfcLevelId, roleId, userId, selectedAdminCompanyCode } = auth;
        if (token) {
            const myHeaders = new Headers();
            myHeaders.append("Authorization", token);
            myHeaders.append("Content-Type", "application/json");

            const effectiveOfficeCode = resolveScopedTableOfficeCode(code, roleId, officeCode, selectedAdminCompanyCode);
            const effectiveOfficeLevel = resolveTableApiOfficeLevel({
                userCode: code,
                effectiveOfficeCode,
                hierarchy,
                cfcLevelId,
                explicitOfficeLevel: officeLevel,
            });
            const resolvedSortOrder =
                Number(sortOrder) === NC_SORT_ORDER_ASC ? NC_SORT_ORDER_ASC : NC_SORT_ORDER_DESC;
            const raw = JSON.stringify({
                pageNumber: String(page || 1),
                pageSize: String(pageSize || 20),
                officeCode: String(effectiveOfficeCode ?? ""),
                officeLevel: String(effectiveOfficeLevel ?? ""),
                overDueFlag: "0",
                dateFrom: dateFrom ? dateFrom : "",
                dateTo: dateTo ? dateTo : "",
                loadFrom: loadFrom !== undefined && loadFrom !== null && loadFrom !== "" ? String(loadFrom) : "",
                loadTo: loadTo !== undefined && loadTo !== null && loadTo !== "" ? String(loadTo) : "",
                applicationProgressId:
                    applicationProgressId === 0 ||
                    applicationProgressId === "0" ||
                    (applicationProgressId !== undefined &&
                        applicationProgressId !== null &&
                        applicationProgressId !== "")
                        ? String(applicationProgressId)
                        : "",
                userId: roleId == 113 || roleId == 1146 ? String(userId ?? "") : "",
                sortOrder: resolvedSortOrder,
            });

            const requestOptions = {
                method: "POST",
                headers: myHeaders,
                body: raw,
                redirect: "follow"
            };

            const response = await fetch(cfpBaseUrl + `enc/getAllApplications`, requestOptions);
            let result;
            try {
                result = await response.json();
            } catch {
                throw new Error('Unable to fetch applications!');
            }

            // Filter / empty result — API returns HTTP or body status 400 "Data Not Found."
            const status = String(result?.status ?? response.status);
            if (status === "400") {
                return {
                    status: "400",
                    message: result?.message || "Data Not Found.",
                    data: [],
                    totalRecords: 0,
                    totalPages: 0,
                };
            }

            if (!response.ok) {
                throw new Error(result?.message || 'Stats API issue!');
            }

            if (status !== "200") {
                throw new Error(result?.message || 'Unable to fetch stats!');
            }

            return result;
        }
    }
);

const newConnectionNormalPaginatedSlice = createSlice({
    name: 'newConnecionNormalPaginated',
    initialState: initialState,
    reducers: {
        nextPage(state) {
            state.currentPage += 1;
        },
        previousPage(state) {
            if (state.currentPage > 1) {
                state.currentPage -= 1;
            }
        },
        filterProgressId(state, action) {
            state.applicationProgressId = action.payload
        },
        resetProgressIdFilter(state) {
            state.applicationProgressId = ''
        },
        filterObjectionStatus(state, action) {
            state.objectionStatus = action.payload
        },
        resetObjectionStatusFilter(state) {
            state.applicationProgressId = ''
        },
        filterDateFrom(state, action) {
            state.dateFrom = action.payload
        },
        filterDateTo(state, action) {
            state.dateTo = action.payload
        },
        resetDateFilter(state) {
            state.dateFrom = ''
            state.dateTo = ''
        },
        filterLoadFrom(state, action) {
            state.loadFrom = action.payload
        },
        filterLoadTo(state, action) {
            state.loadTo = action.payload
        },
        resetLoadFilter(state) {
            state.loadFrom = ''
            state.loadTo = ''
        },
        filterSortOrder(state, action) {
            const next = Number(action.payload);
            state.sortOrder =
                next === NC_SORT_ORDER_ASC ? NC_SORT_ORDER_ASC : NC_SORT_ORDER_DESC;
        },
        resetSortOrder(state) {
            state.sortOrder = NC_SORT_ORDER_DESC;
        },
        firstPage(state) {
            state.currentPage = 1;
        },
        lastPage(state, action) {
            state.currentPage = action.payload;
        },
        // Clears every table filter (used when leaving the page). Back to page 1 only if a filter
        // was set, since the page number belonged to the filtered list.
        resetFilters(state) {
            const isSet = (value) => value !== '' && value != null
            const hadFilter =
                [state.dateFrom, state.dateTo, state.loadFrom, state.loadTo, state.applicationProgressId, state.objectionStatus].some(isSet) ||
                Number(state.sortOrder) === NC_SORT_ORDER_ASC
            state.dateFrom = ''
            state.dateTo = ''
            state.loadFrom = ''
            state.loadTo = ''
            state.applicationProgressId = ''
            state.objectionStatus = ''
            state.sortOrder = NC_SORT_ORDER_DESC
            if (hadFilter) state.currentPage = 1
        },

        resetState(state) {
            state.data = []
            state.currentPage = 1
            state.pageSize = 20
            state.dateFrom = ''
            state.dateTo = ''
            state.loadFrom = ''
            state.loadTo = ''
            state.applicationProgressId = ''
            state.objectionStatus = ''
            state.sortOrder = NC_SORT_ORDER_DESC
            state.totalPages = null
            state.totalRecords = null
            state.isLoading = false
            state.error = null
            state.latestRequestId = null
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchPaginatedNewConnectionNormal.pending, (state, action) => {
                state.isLoading = true;
                state.latestRequestId = action.meta.requestId;
            })
            .addCase(fetchPaginatedNewConnectionNormal.fulfilled, (state, action) => {
                // An older request finishing late (e.g. one with a filter that has since been
                // changed or cleared) must not overwrite the newer list
                if (action.meta.requestId !== state.latestRequestId) return;
                state.isLoading = false;
                state.error = null;

                const payload = action.payload || {};
                const rows = Array.isArray(payload.data) ? payload.data : [];
                state.data = rows;

                let totalRecords = Number(
                    payload.totalRecords ?? payload.totalCount ?? payload.TotalRecords
                );
                let totalPages = Number(
                    payload.totalPages ?? payload.TotalPages
                );

                if (!Number.isFinite(totalRecords) || totalRecords < 0) {
                    totalRecords = 0;
                }
                if (!Number.isFinite(totalPages) || totalPages < 0) {
                    totalPages = 0;
                }

                if (totalRecords === 0 && rows.length > 0) {
                    const previous = Number(state.totalRecords);
                    totalRecords = Number.isFinite(previous) && previous > rows.length
                        ? previous
                        : rows.length;
                }

                if (totalPages === 0 && totalRecords > 0) {
                    totalPages = Math.max(
                        1,
                        Math.ceil(totalRecords / (state.pageSize || 20))
                    );
                }

                if (totalRecords === 0 && totalPages > 1) {
                    totalRecords = totalPages * (state.pageSize || 20);
                }

                state.totalRecords = totalRecords;
                state.totalPages = totalPages || 1;
            })
            .addCase(fetchPaginatedNewConnectionNormal.rejected, (state, action) => {
                if (action.meta.requestId !== state.latestRequestId) return;
                state.isLoading = false;
                state.error = action.error.message;
            });
    },
});

export const { nextPage, previousPage, firstPage, lastPage, filterProgressId, filterObjectionStatus, filterDateFrom, filterDateTo, resetProgressIdFilter, resetObjectionStatusFilter, resetDateFilter, resetState, filterLoadFrom, filterLoadTo, resetLoadFilter, filterSortOrder, resetSortOrder, resetFilters } = newConnectionNormalPaginatedSlice.actions;

export default newConnectionNormalPaginatedSlice.reducer;
